import { AdapterResult, ParsedTxn, BalancePoint } from '../money.model';
import { parseUsDate, parseAmountCents, hash64 } from './parse-values';
import { normalizeBank } from './normalize';

const col = (header: string[], name: string): number =>
  header.findIndex(h => (h ?? '').replace(/^﻿/, '').trim().toLowerCase() === name.toLowerCase());

/** The export already masks the account number, so no full number ever exists here. */
export function bankAccountId(accountNumber: string): string {
  return 'bank-' + hash64('bank|' + (accountNumber ?? '').trim());
}

export function bankMask(accountNumber: string): string {
  const m = /(\d{3,})/.exec(accountNumber ?? '');
  return m ? m[1].slice(-4) : '';
}

export function adaptBank(rows: string[][]): AdapterResult {
  const [header, ...body] = rows;
  const iAcct    = col(header, 'Account Number');
  const iDate    = col(header, 'Post Date');
  const iCheck   = col(header, 'Check');
  const iDesc    = col(header, 'Description');
  const iDebit   = col(header, 'Debit');
  const iCredit  = col(header, 'Credit');
  const iStatus  = col(header, 'Status');
  const iBalance = col(header, 'Balance');

  const txns: ParsedTxn[] = [];
  const balances: BalancePoint[] = [];
  const seenBalanceDates = new Set<string>();
  let pendingCount = 0;
  let accountMask = '';

  for (const r of body) {
    if ((r[iDate] ?? '').trim() === '') continue;

    if ((r[iStatus] ?? '').trim().toLowerCase() === 'pending') { pendingCount++; continue; }

    const accountNumber = (r[iAcct] ?? '').trim();
    const accountId = bankAccountId(accountNumber);
    if (!accountMask) accountMask = bankMask(accountNumber);

    const date = parseUsDate(r[iDate]);
    const creditRaw = (r[iCredit] ?? '').trim();
    const amount = creditRaw !== ''
      ? parseAmountCents(creditRaw)
      : -parseAmountCents(r[iDebit]);

    const check = (r[iCheck] ?? '').trim();
    const rawDesc = r[iDesc] ?? '';
    const description = check ? `${rawDesc} Check ${check}` : rawDesc;

    txns.push({
      date, amount, description,
      merchant: '',
      matchKey: normalizeBank(description),
      accountId,
      source: 'bank',
      isTransfer: false,           // decided by the rules engine
      purchasedBy: '',
      sourceCategory: '',
    });

    // Rows are newest-first, so the FIRST row seen for a date carries that
    // day's closing balance — but only if it reports one. An empty Balance
    // cell is "not reported", not "zero": the export leaves it blank on ACH
    // prenotification rows, and parseAmountCents('') returns 0, which would
    // record a $0.00 balance the account never had. The date is marked seen
    // only once a balance is actually taken, so a blank leading row lets the
    // next row of the same date supply the real figure.
    const balanceRaw = (r[iBalance] ?? '').trim();
    if (balanceRaw !== '' && !seenBalanceDates.has(date)) {
      seenBalanceDates.add(date);
      balances.push({ accountId, date, balance: parseAmountCents(balanceRaw) });
    }
  }

  return { source: 'bank', txns, balances, pendingCount, accountMask, accountKind: 'checking' };
}
